import { EventEntry } from "@/src/constants/mock-events.constant";
import * as FileSystem from "expo-file-system/legacy";
import * as Sharing from "expo-sharing";
import { Platform } from "react-native";
import { utils, write } from "xlsx";

type ParticipantStatus = "attending" | "waitlisted";

const EXCEL_SHEET_NAME = "Participants";
const EXCEL_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const EXCEL_UTI = "com.microsoft.excel.xlsx";

const EXCEL_COLUMN_WIDTHS = [
  { wch: 24 },
  { wch: 30 },
  { wch: 14 },
  { wch: 16 },
  { wch: 8 },
];

/**
 * Writes a single-sheet workbook and hands it to the platform's download/share
 * flow. Shared by every Excel export in the app so the web-vs-native branching
 * (Blob+anchor vs. FileSystem+Sharing) exists in exactly one place.
 */
export const downloadRowsAsExcel = async (
  rows: Record<string, unknown>[],
  fileName: string,
  sheetName: string = EXCEL_SHEET_NAME,
  columnWidths?: { wch: number }[],
): Promise<void> => {
  const worksheet = utils.json_to_sheet(rows);
  if (columnWidths) worksheet["!cols"] = columnWidths;

  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, sheetName);

  if (Platform.OS === "web") {
    const buffer = write(workbook, { type: "array", bookType: "xlsx" });
    const blob = new Blob([buffer], { type: EXCEL_MIME_TYPE });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
    return;
  }

  const base64 = write(workbook, { type: "base64", bookType: "xlsx" });
  const fileUri = `${FileSystem.cacheDirectory}${fileName}`;

  await FileSystem.writeAsStringAsync(fileUri, base64, {
    encoding: "base64",
  });

  await Sharing.shareAsync(fileUri, {
    mimeType: EXCEL_MIME_TYPE,
    dialogTitle: `Export ${fileName}`,
    UTI: EXCEL_UTI,
  });
};

export const exportParticipantsToExcel = async (
  event: EventEntry,
  participantStatus: ParticipantStatus,
): Promise<void> => {
  const rows = (event?.participants ?? [])
    .filter((participant) => participant?.attendingStatus === participantStatus)
    .map((participant) => ({
      Name: participant?.displayName,
      Email: participant?.email,
      Status: participant?.attendingStatus,
      Guests: participant?.guestCount ?? 0,
    }));

  const fileName = `${event?.title.replace(/\s+/g, "_")}_participants.xlsx`;
  await downloadRowsAsExcel(rows, fileName, EXCEL_SHEET_NAME, EXCEL_COLUMN_WIDTHS);
};
